import { StarOutlineOutlined as StarOutlineOutlinedIcon } from 'components/icons';
import MatterIndicators from 'components/matters/MatterIndicators';
import StatusChip from 'components/@extended/StatusChip.tsx';
import { buttonStyle } from 'components/buttons/buttonStyle';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader.tsx';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useLazyGetExperienceRecordsWithFiltersQuery } from 'store/api/experience-records';
import { MatterExperienceRecordDto } from 'types/experience-records.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState.ts';
import { getStatusChipType } from '../matters/util.tsx';
import { AssignedEmployeesCell } from './components/AssignedEmployeesCell.tsx';
import { OpenedDateCell } from './components/OpenedDateCell.tsx';
import { XrConfidentialMarker } from './components/XrConfidentialMarker';
import { XrDraftChip } from './components/XrDraftChip';
import { getExperienceRecordHref } from './ExperienceRecordsTableWithFilters';

const ENTITY_LINK = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

type Props = {
    createXrNavigationUrl?: string;
    passiveFilter?: string;
    canRememberFilters?: boolean;
};

const ExperienceRecordsTableReadOnly = ({
    createXrNavigationUrl,
    passiveFilter = '',
    canRememberFilters = true,
}: Props) => {
    const navigateWithReturn = useNavigateWithReturnState();

    const [fetchData, { data, isFetching }] = useLazyGetExperienceRecordsWithFiltersQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['MATTER_STATE_TYPE', 'COUNTRY'],
    });

    const columns = useMemo<DataTableColumnDef<MatterExperienceRecordDto>[]>(
        () => [
            {
                id: 'matterExperienceRecord',
                header: t('experience-records.list-page.table.xr-name-description'),
                accessorFn: (dto) => dto.experienceRecord?.name,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 220,
                },
                cell: ({ row }) => {
                    const { experienceRecord, matter } = row.original;
                    if (!matter.isReadable) return null;

                    if (!experienceRecord) {
                        return (
                            <Button
                                {...buttonStyle({ variant: 'outlined', size: 'small' }, 'text-body2')}
                                onClick={() => navigateWithReturn(`/matter/${matter.id}/experience-records/new`)}
                            >
                                <FormattedMessage id="experience-records.list-page.add-new" />
                            </Button>
                        );
                    }

                    return (
                        <div className="flex flex-col gap-1">
                            <div className="flex gap-1">
                                {experienceRecord.status === 'DRAFT' && <XrDraftChip />}
                                {experienceRecord.isConfidential && (
                                    <XrConfidentialMarker label={t('experience-records.view.header.confidential')} />
                                )}
                                <Link
                                    className={`${ENTITY_LINK} font-semibold`}
                                    to={`/experience-records/${experienceRecord.id}`}
                                >
                                    {experienceRecord.name}
                                </Link>
                            </div>
                            {experienceRecord.description && (
                                <Tooltip content={experienceRecord.description}>
                                    <p className="text-body2 text-fg-secondary line-clamp-2">
                                        {experienceRecord.description}
                                    </p>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'matterLink',
                header: t('experience-records.list-page.table.case-status'),
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 220,
                },
                cell: ({ row }) => {
                    const { matter, experienceRecord } = row.original;
                    return matter.isReadable ? (
                        <div className="flex flex-col gap-1">
                            <RecordLink
                                withOrigin
                                className={`${ENTITY_LINK} font-semibold`}
                                to={`/matter/${matter.id}`}
                                label={matter.fullName ?? '-'}
                            />
                            <div className="flex items-center gap-1">
                                <MatterIndicators isConfidential={matter.isConfidential} variant="chip" />
                                <StatusChip
                                    type={getStatusChipType(matter.matterStateTypeCode)}
                                    label={getClassifierText(
                                        classifiers['MATTER_STATE_TYPE'],
                                        matter.matterStateTypeCode
                                    )}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-row gap-4">
                            <MatterIndicators isConfidential={matter.isConfidential} hasAccess={false} variant="chip" />
                            <div className="flex flex-col gap-1">
                                <p className="text-body1 font-semibold whitespace-nowrap">{matter.fullName ?? '-'}</p>
                                <p className="text-body1 font-semibold whitespace-nowrap">
                                    {experienceRecord?.name ?? '-'}
                                </p>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: 'matterEmployees',
                header: t('experience-records.list-page.table.assigned-to'),
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 220,
                },
                cell: ({ row }) => <AssignedEmployeesCell record={row.original} />,
            },
            {
                id: 'matterOffice',
                header: t('experience-records.list-page.table.opening-date-office'),
                accessorFn: (dto) => dto.matter.openedDate,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 220,
                },
                cell: ({ row }) => {
                    return <OpenedDateCell record={row.original} />;
                },
            },
        ],
        [classifiers, navigateWithReturn]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            passiveFilter,
            searchBarSelectors: [
                { selector: 'matter.name' },
                { selector: 'matter.caseNumber' },
                { selector: 'matterClientParty.name' },
                { selector: 'experienceRecordName' },
                { selector: 'experienceRecordDescription' },
            ],
        }),
        [passiveFilter]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<MatterExperienceRecordDto>
            getRowHref={getExperienceRecordHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="experience-records-read-only"
            columns={columns}
            data={data}
            isLoading={isFetching}
            singularTextId="experience-records.list-page.table.record-singular"
            pluralTextId="experience-records.list-page.table.record-plural"
            noEntriesId="experience-records.list-page.table.no-records-found"
            noEntriesBodyId="experience-records.empty.body"
            noEntriesIcon={StarOutlineOutlinedIcon}
            noEntriesAction={
                createXrNavigationUrl != null ? (
                    <ListCreateButton
                        permissions={['CREATE.EXPERIENCE_RECORDS.ALL']}
                        variant="contained"
                        color="secondary"
                        onClick={() => navigateWithReturn(createXrNavigationUrl)}
                        labelId="experience-records.list-page.add-new"
                        newLabelId="new-entity.experience-record"
                    />
                ) : null
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            extraHeaderComponent={
                createXrNavigationUrl != null && (
                    <ListCreateButton
                        permissions={['CREATE.EXPERIENCE_RECORDS.ALL']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(createXrNavigationUrl)}
                        labelId="experience-records.list-page.add-new"
                        newLabelId="new-entity.experience-record"
                    />
                )
            }
            canRememberFilters={canRememberFilters}
        />
    );
};

export default ExperienceRecordsTableReadOnly;
